/**
 * Admin settings card for Clarik AI (typed-chat assistant).
 *
 * Renders inside the existing AI/automation settings tab. Mirrors the
 * server-side `voiceAssistantSettings` shape. Spoken voice is hard-off for
 * beta (Task #427): the server kill-switch forces every session to the typed
 * "disabled" provider regardless of this card, so the provider field is no
 * longer surfaced here — admins only toggle availability, roles, and persona.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Mic } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type VoiceAssistantSettings = {
  workspaceId: string;
  enabled: boolean;
  provider: "realtime" | "disabled";
  allowActions: boolean;
  enabledRoles: string[];
  voicePersona: string;
  mobileButtonMode: "drawer" | "voice_direct" | "voice_panel";
};

type VoiceAssistantConfig = {
  settings: VoiceAssistantSettings | null;
  role: string;
  isSuperAdmin: boolean;
  toolSurface: Array<{ name: string; description: string; isWrite: boolean }>;
};

const ALL_ROLES: { value: string; label: string }[] = [
  { value: "admin", label: "Admin" },
  { value: "sales_rep", label: "Sales Rep" },
  { value: "artist", label: "Talent" },
  { value: "billing", label: "Billing" },
  // v2: { value: "marketing", label: "Marketing" },
];

const PERSONAS = ["alloy", "echo", "fable", "onyx", "nova", "shimmer"];

export function VoiceAssistantSettingsCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<VoiceAssistantConfig>({
    queryKey: ["/api/voice-assistant/config"],
  });

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<"realtime" | "disabled">("disabled");
  const [allowActions, setAllowActions] = useState(true);
  const [roles, setRoles] = useState<string[]>([
    "admin",
    "sales_rep",
    "artist",
    "billing",
    "marketing",
  ]);
  const [persona, setPersona] = useState<string>("alloy");
  const [mobileButtonMode, setMobileButtonMode] = useState<"drawer" | "voice_direct" | "voice_panel">("drawer");

  useEffect(() => {
    const s = data?.settings;
    if (!s) return;
    setEnabled(!!s.enabled);
    setProvider((s.provider as any) ?? "disabled");
    setAllowActions(s.allowActions !== false);
    setRoles(Array.isArray(s.enabledRoles) ? s.enabledRoles : []);
    setPersona(s.voicePersona ?? "alloy");
    setMobileButtonMode((s.mobileButtonMode as any) ?? "drawer");
  }, [data]);

  const toggleEnabled = useMutation({
    mutationFn: async (next: boolean) => {
      return apiRequest("PATCH", "/api/voice-assistant/settings", { enabled: next });
    },
    onSuccess: (_data, next) => {
      setEnabled(next);
      queryClient.invalidateQueries({ queryKey: ["/api/voice-assistant/config"] });
    },
    onError: (err: any) => {
      toast({
        title: "Could not update",
        description: err?.message ?? "Failed to toggle Clarik AI",
        variant: "destructive",
      });
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      return apiRequest(
        "PATCH",
        "/api/voice-assistant/settings",
        { enabled, provider, allowActions, enabledRoles: roles, voicePersona: persona, mobileButtonMode },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/voice-assistant/config"] });
      toast({ title: "Voice assistant settings saved" });
    },
    onError: (err: any) => {
      toast({
        title: "Save failed",
        description: err?.message ?? "Could not save settings",
        variant: "destructive",
      });
    },
  });

  function toggleRole(role: string, checked: boolean) {
    setRoles((cur) =>
      checked ? Array.from(new Set([...cur, role])) : cur.filter((r) => r !== role),
    );
  }

  return (
    <Card data-testid="card-voice-assistant-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="w-5 h-5 text-primary" />
          Clarik AI
        </CardTitle>
        <CardDescription>
          Lets users type to the assistant from anywhere in the app. Each user
          can only see their own scoped data — artists see their own events,
          sales reps see their own pipeline, admins see the whole workspace.
          Spoken voice is off for beta; the assistant is typed-chat only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <Label htmlFor="va-enabled" className="flex flex-col">
                <span>Enabled</span>
                <span className="text-xs text-muted-foreground font-normal">
                  Master switch. When off, the floating assistant button is
                  hidden for everyone.
                </span>
              </Label>
              <Switch
                id="va-enabled"
                checked={enabled}
                onCheckedChange={(v) => toggleEnabled.mutate(v)}
                disabled={toggleEnabled.isPending}
                data-testid="switch-va-enabled"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="va-actions" className="flex flex-col">
                <span>Allow write actions</span>
                <span className="text-xs text-muted-foreground font-normal">
                  When on, the assistant can send emails, create follow-ups, mark
                  invoices paid, and add notes — always with a spoken confirmation.
                </span>
              </Label>
              <Switch
                id="va-actions"
                checked={allowActions}
                onCheckedChange={setAllowActions}
                data-testid="switch-va-actions"
              />
            </div>

            <div>
              <Label className="mb-2 block">Roles that can use it</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_ROLES.map((r) => (
                  <div
                    key={r.value}
                    className="flex items-center space-x-2"
                    data-testid={`row-va-role-${r.value}`}
                  >
                    <Checkbox
                      id={`va-role-${r.value}`}
                      checked={roles.includes(r.value)}
                      onCheckedChange={(c) => toggleRole(r.value, c === true)}
                      data-testid={`checkbox-va-role-${r.value}`}
                    />
                    <Label htmlFor={`va-role-${r.value}`} className="font-normal">
                      {r.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block" htmlFor="va-persona">
                Voice persona
              </Label>
              <Select value={persona} onValueChange={setPersona}>
                <SelectTrigger
                  id="va-persona"
                  className="w-48"
                  data-testid="select-va-persona"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSONAS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => save.mutate()}
                disabled={save.isPending}
                data-testid="button-save-va-settings"
              >
                {save.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
